import React from 'react';
import { Zap, Shield, Globe, MousePointer, Hammer, Rocket, Code2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { createPageUrl } from '@/utils';
import SEOHead from '@/components/SEOHead';

// ─── Data ─────────────────────────────────────────────────────────────────────

const PILLARS = [
  { icon: Zap, title: 'Velocity', desc: 'Unprecedented delivery speed. We deploy production-ready systems in days, not months.' },
  { icon: Shield, title: 'Excellence', desc: 'Zero technical debt. Premium architecture built on elite, modern frameworks.' },
  { icon: Code2, title: 'Bespoke', desc: 'No templates. Every line of code is engineered specifically for your business goals.' },
  { icon: Globe, title: 'Global Scale', desc: 'Edge-deployed networks that load instantly for users across the world.' },
];

const PIPELINE = [
  { step: '01', icon: MousePointer, title: 'Architect & Scope', desc: 'We map out your data logic, target audience, and precise technical requirements.' },
  { step: '02', icon: Hammer, title: 'Iterative Build', desc: 'Rapid, aggressive development loops. Real-time staging links pushed to your inbox.' },
  { step: '03', icon: Rocket, title: 'Edge Deployment', desc: 'Push to global CDNs. Flawless scalability from day one with zero downtime.' },
];

const STATS = [
  { value: '40+', label: 'Digital Products Shipped' },
  { value: '99.9%', label: 'Uptime SLA' },
  { value: '15+', label: 'Global Enterprise Clients' },
  { value: '0ms', label: 'Compromise on Quality' },
];

// ─── Component ────────────────────────────────────────────────────────────────

export default function About() {
  return (
    <div className="min-h-screen bg-zinc-950 text-white pt-20 overflow-hidden">
      <SEOHead
        title="About Us"
        description="Learn about BuildersLab's mission to democratise web development with AI. Meet our team and discover how we're changing the way websites are built."
        ogUrl="https://builderslab.net/About"
      />
      
      {/* ── Background Ambience ── */}
      <div className="pointer-events-none fixed inset-0">
        <div className="absolute top-0 right-0 w-[800px] h-[800px] bg-[radial-gradient(ellipse_at_center,rgba(249,115,22,0.04)_0%,transparent_60%)]" />
        <div className="absolute top-1/2 left-0 w-[600px] h-[600px] bg-[radial-gradient(ellipse_at_center,rgba(59,130,246,0.03)_0%,transparent_60%)] -translate-y-1/2" />
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI0MCIgaGVpZ2h0PSI0MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAwIDEwIEwgNDAgMTAgTSAxMCAwIEwgMTAgNDAiIGZpbGw9Im5vbmUiIHN0cm9rZT0icmdiYSgyNTUsMjU1LDI1NSwwLjAyKSIgc3Ryb2tlLXdpZHRoPSIxIi8+PC9wYXR0ZXJuPjwvZGVmcz48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSJ1cmwoI2dyaWQpIi8+PC9zdmc+')] [mask-image:radial-gradient(ellipse_at_center,black_40%,transparent_80%)] opacity-30" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6">

        {/* ── Header ── */}
        <section className="py-24 text-center">
          <div
           
           
           
          >
             <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-orange-500/10 border border-orange-500/20 mb-6">
              
              <span className="text-orange-400 text-sm font-semibold tracking-wide">Our Manifesto</span>
            </div>

            <h1 className="text-5xl md:text-7xl font-extrabold tracking-tighter text-white mb-6">
              We engineer <br className="hidden md:block"/>
              <span className="bg-gradient-to-r from-orange-400 via-amber-300 to-orange-500 bg-clip-text text-transparent">
                digital dominance.
              </span>
            </h1>

            <p className="text-zinc-400 text-lg md:text-xl font-light max-w-2xl mx-auto leading-relaxed">
              BuildersLab isn't just an agency. We are a specialized collective of software architects obsessed with velocity, aesthetics, and flawless execution.
            </p>
          </div>
        </section>

        {/* ── Stats Strip ── */}
        <section className="mb-32">
          <div 
           
           
           
            className="grid grid-cols-2 md:grid-cols-4 border border-zinc-800 rounded-3xl overflow-hidden bg-zinc-900/40 backdrop-blur-md"
          >
            {STATS.map((stat, i) => (
              <div 
                key={stat.label} 
                className={`p-8 md:p-10 text-center ${i !== 3 ? 'border-r border-zinc-800/50' : ''} ${i < 2 ? 'border-b md:border-b-0 border-zinc-800/50' : ''}`}
              >
                <div className="text-4xl md:text-5xl font-black text-white tracking-tighter mb-2">{stat.value}</div>
                <div className="text-sm font-bold text-zinc-500 uppercase tracking-widest">{stat.label}</div>
              </div>
            ))}
          </div>
        </section>

        {/* ── Advantage (Why Us) ── */}
        <section className="mb-32 relative">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">The Advantage</h2>
            <p className="text-zinc-400 max-w-xl mx-auto">Why founders and enterprises choose our framework over legacy solutions.</p>
          </div>

          <div className="grid sm:grid-cols-2 gap-6 lg:gap-8">
            {PILLARS.map((pillar, i) => (
              <div
                key={pillar.title}
               
               
               
               
                className="group relative rounded-3xl bg-zinc-900/50 border border-zinc-800 p-8 hover:bg-zinc-900/80 transition-all overflow-hidden"
              >
                {/* Hover Glow */}
                <div className="absolute top-0 right-0 w-32 h-32 bg-orange-500/10 blur-[40px] opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" />
                
                <div className="relative z-10 flex items-start gap-6">
                  <div className="w-14 h-14 rounded-2xl bg-white/5 border border-white/10 flex items-center justify-center shrink-0 group-hover:bg-orange-500/10 group-hover:border-orange-500/20 transition-colors">
                    <pillar.icon className="w-6 h-6 text-zinc-400 group-hover:text-orange-400 transition-colors" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-white mb-2">{pillar.title}</h3>
                    <p className="text-zinc-400 leading-relaxed font-light">{pillar.desc}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* ── Pipeline (Process) ── */}
        <section className="mb-32">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-8 mb-16">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">The Pipeline</h2>
              <p className="text-zinc-400 max-w-xl">Our ruthlessly optimized process for getting products to market.</p>
            </div>
            
            <Link to={createPageUrl('Team')}>
              <button className="flex items-center gap-2 px-6 py-3 rounded-xl bg-zinc-900 border border-zinc-800 text-white font-semibold hover:bg-zinc-800 transition-colors group">
                Meet the Team
              </button>
            </Link>
          </div>

          <div className="relative">
            {/* Connection Line */}
            <div className="hidden lg:block absolute top-[4.5rem] left-[16%] right-[16%] h-[1px] bg-zinc-800 overflow-hidden z-0">
              <div 
               
               
                className="h-full w-1/4 bg-gradient-to-r from-transparent via-orange-500 to-transparent"
              />
            </div>

            <div className="grid md:grid-cols-3 gap-12 lg:gap-8 relative z-10">
              {PIPELINE.map((step, i) => (
                <div
                  key={step.step}
                 
                 
                 
                 
                  className="relative text-center group"
                >
                  <div className="relative mx-auto w-36 h-36 mb-8 flex items-center justify-center">
                    {/* Ring */}
                    <div className="absolute inset-0 rounded-full border border-zinc-800 bg-zinc-950 group-hover:border-orange-500/50 group-hover:bg-orange-500/5 transition-all shadow-xl group-hover:shadow-[0_0_30px_rgba(249,115,22,0.15)]" />
                    
                    <step.icon className="relative z-10 w-10 h-10 text-zinc-500 group-hover:text-orange-400 transition-colors" />

                    {/* Step Number Badge */}
                    <div className="absolute top-0 right-2 w-10 h-10 bg-white rounded-full flex items-center justify-center text-sm font-black text-zinc-950 border-4 border-zinc-950 shadow-xl group-hover:bg-orange-500 group-hover:text-white transition-colors">
                      {step.step}
                    </div>
                  </div>

                  <h3 className="text-2xl font-bold text-white mb-3">{step.title}</h3>
                  <p className="text-zinc-400 leading-relaxed font-light">{step.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── Footer CTA ── */}
        <section className="pb-32">
          <div
           
           
           
            className="relative bg-gradient-to-br from-orange-500 to-amber-600 rounded-3xl p-12 md:p-16 text-center shadow-[0_0_50px_rgba(249,115,22,0.2)] overflow-hidden"
          >
            {/* Grain overlay */}
            <div className="absolute inset-0 opacity-10 bg-[url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0naHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmcnIHdpZHRoPSc0MDAlJyBoZWlnaHQ9JzQwMCUnPgo8ZmlsdGVyIGlkPSdub2lzZSc+PGZlVHVyYnVsZW5jZSB0eXBlPSdmcmFjdGFsTm9pc2UnIGJhc2VGcmVxdWVuY3k9JzAuOScgbnVtT2N0YXZlcz0nMycgc3RpdGNoVGlsZXM9J3N0aXRjaCcvPjwvZmlsdGVyPgo8cmVjdCB3aWR0aD0nMTAwJScgaGVpZ2h0PScxMDAlJyBmaWx0ZXI9J3VybCgjbm9pc2UpJy8+Cjwvc3ZnPg==')]" />
            
            <div className="relative z-10">
              <h2 className="text-3xl md:text-5xl font-black text-white mb-6 tracking-tight">
                Stop planning. Start building.
              </h2>
              <p className="text-orange-100 text-lg md:text-xl font-medium mb-10 max-w-2xl mx-auto">
                Join the fastest-moving teams in the world relying on BuildersLab architecture.
              </p>
              
              <Link to={createPageUrl('Contact')}>
                <button className="h-14 px-8 rounded-xl bg-zinc-950 text-white font-bold text-sm tracking-wide shadow-2xl hover:shadow-[0_0_30px_rgba(0,0,0,0.5)] hover:bg-zinc-900 transition-all flex items-center gap-2 mx-auto group">
                  Initiate Project
                </button>
              </Link>
            </div>
          </div>
        </section>

      </div>
    </div>
  );
}